import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getEmployeeBenefits, getCompanyBenefits, updateEmployeeBenefit } from 'services/benefits';
import { createNotification } from 'services/timeOff';
import { createOffboarding } from 'services/offboarding';
import { toast } from 'components/Toast';
import routes from 'constants/routes';

const REASONS = [
    { value: 'voluntary', label: 'Voluntary Resignation' },
    { value: 'involuntary', label: 'Involuntary Termination' },
    { value: 'layoff', label: 'Layoff / Reduction in Force' },
    { value: 'retirement', label: 'Retirement' },
    { value: 'contract_end', label: 'End of Contract' },
    { value: 'other', label: 'Other' },
];

const FINAL_PAY = [
    { value: 'next_regular', label: 'Include in next regular payroll', desc: 'Final wages on the next scheduled payroll.' },
    { value: 'off_cycle', label: 'Create off-cycle / one-off payroll', desc: 'Go to Run Payroll after this wizard.' },
    { value: 'outside', label: 'Paid outside PayrollNoot', desc: 'Record that final pay was handled elsewhere.' },
];

const STEP_LABELS = ['Confirm', 'Final Pay', 'Benefits', 'Portal Access', 'Summary'];
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }) : '—';

function StepDots({ current, total }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
            {Array.from({ length: total }).map((_, i) => (
                <React.Fragment key={i}>
                    <div style={{
                        width: i === current ? 28 : 10, height: 10, borderRadius: 99,
                        background: i < current ? '#16a34a' : i === current ? '#1B75F0' : '#e5e7eb',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        {i < current && <span style={{ color: '#fff', fontSize: 8 }}>✓</span>}
                    </div>
                    {i < total - 1 && <div style={{ flex: 1, maxWidth: 28, height: 2, background: i < current ? '#16a34a' : '#e5e7eb' }} />}
                </React.Fragment>
            ))}
        </div>
    );
}

/**
 * Guided offboarding for employee or contractor.
 * @param {{ emp: object, workerType?: 'employee'|'contractor', onClose: fn, onDone?: fn }} props
 */
export default function TerminationModal({ emp, workerType = 'employee', onClose, onDone }) {
    const isContractor = workerType === 'contractor';
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [termDate, setTermDate] = useState(new Date().toISOString().slice(0, 10));
    const [reason, setReason] = useState('voluntary');
    const [notes, setNotes] = useState('');
    const [rehireEligible, setRehireEligible] = useState(true);
    const [finalPayPath, setFinalPayPath] = useState('next_regular');
    const [ptoAmount, setPtoAmount] = useState('');
    const [deactivateBenefits, setDeactivateBenefits] = useState({});
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);
    const [results, setResults] = useState(null);

    const { data: allEmpBenefits = [] } = useQuery({
        queryKey: ['employee-benefits'],
        queryFn: getEmployeeBenefits,
        staleTime: 60000,
        enabled: !isContractor,
    });
    const { data: companyBenefits = [] } = useQuery({
        queryKey: ['company-benefits'],
        queryFn: getCompanyBenefits,
        staleTime: 60000,
        enabled: !isContractor,
    });

    const empBenefits = useMemo(() => {
        if (isContractor) return [];
        const planMap = {};
        companyBenefits.forEach(p => { planMap[p.id] = p; });
        return allEmpBenefits
            .filter(eb => eb.employeeId === emp.id && eb.active !== false)
            .map(eb => ({
                ...eb,
                planName: planMap[eb.companyBenefitId]?.description
                    || planMap[eb.companyBenefitId]?.name
                    || eb.description || eb.type || '—',
            }));
    }, [allEmpBenefits, companyBenefits, emp.id, isContractor]);

    useEffect(() => {
        const init = {};
        empBenefits.forEach(eb => { init[eb.id] = true; });
        setDeactivateBenefits(init);
    }, [empBenefits.length]); // eslint-disable-line

    const cobraDeadline = useMemo(() => {
        const d = new Date(termDate);
        d.setDate(d.getDate() + 60);
        return fmt(d.toISOString());
    }, [termDate]);

    const cobraBenefits = empBenefits.filter(eb => {
        const t = (eb.type || '').toLowerCase();
        return ['medical', 'dental', 'vision', 'health'].some(x => t.includes(x));
    });

    const personName = [emp.firstName, emp.lastName].filter(Boolean).join(' ');
    const companyName = localStorage.getItem('companyName') || 'Your Employer';
    const today = new Date().toISOString().slice(0, 10);
    const isScheduled = termDate > today;

    const handleFinish = async () => {
        setSaving(true);
        const benefitsDeactivated = [];
        const benefitsFailed = [];
        try {
            if (!isContractor) {
                for (const eb of empBenefits) {
                    if (deactivateBenefits[eb.id]) {
                        try {
                            await updateEmployeeBenefit({ benefitId: eb.id, active: false });
                            benefitsDeactivated.push(eb.planName);
                        } catch {
                            benefitsFailed.push(eb.planName);
                        }
                    }
                }
            }

            const record = await createOffboarding({
                workerType: isContractor ? 'contractor' : 'employee',
                workerId: emp.id,
                effectiveDate: termDate,
                reason,
                notes: notes || null,
                rehireEligible,
                finalPayPath,
                ptoPayoutAmount: ptoAmount === '' ? null : Number(ptoAmount),
            });

            if (!isScheduled) {
                try {
                    await createNotification({
                        recipientId: emp.id,
                        recipientType: isContractor ? 'contractor' : 'employee',
                        type: 'termination',
                        title: `Your ${isContractor ? 'contract' : 'employment'} at ${companyName} has ended`,
                        message: `Your last day was ${fmt(termDate)}. Portal access ends on the effective date. Contact HR with questions.`,
                        link: isContractor ? '/contractor-portal' : '/employee/paystubs',
                    });
                } catch (_) { /* optional */ }
            }

            queryClient.invalidateQueries({ queryKey: ['employees'] });
            queryClient.invalidateQueries({ queryKey: ['contractors'] });
            queryClient.invalidateQueries({ queryKey: ['offboarding'] });
            queryClient.invalidateQueries({ queryKey: ['employee-benefits'] });

            setResults({
                benefitsDeactivated,
                benefitsFailed,
                termDate,
                reason,
                finalPayPath,
                isScheduled,
                recordId: record?.id || record?.Id,
                status: record?.status || record?.Status,
            });
            setStep(4);
            setDone(true);
            toast(
                isScheduled
                    ? `${personName} offboarding scheduled for ${fmt(termDate)}.`
                    : `${personName} has been offboarded successfully.`,
                'success'
            );
            onDone?.(record);
        } catch (e) {
            toast(e?.response?.data?.message || e?.message || 'Offboarding failed.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 };
    const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }} onClick={e => { if (e.target === e.currentTarget && !saving) onClose(); }}>
            <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', overflow: 'hidden', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: 'linear-gradient(135deg, #7f1d1d, #b91c1c)', padding: '20px 24px' }}>
                    <div style={{ color: '#fca5a5', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Guided Offboarding</div>
                    <div style={{ color: '#fff', fontSize: 18, fontWeight: 700, marginTop: 4 }}>{personName}</div>
                    <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, marginTop: 2 }}>{isContractor ? 'Contractor' : 'Employee'} · {STEP_LABELS[step]}</div>
                </div>

                <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
                    {!done && <StepDots current={step} total={5} />}

                    {step === 0 && (
                        <div>
                            <p style={{ fontSize: 14, color: '#475569', marginTop: 0 }}>
                                Begin offboarding for <strong>{personName}</strong>. Set an effective last day (today, past, or future).
                            </p>
                            <label style={labelStyle}>Effective last day *</label>
                            <input type="date" value={termDate} onChange={e => setTermDate(e.target.value)} style={inputStyle} />
                            {isScheduled && (
                                <p style={{ fontSize: 12, color: '#b45309', marginTop: 8 }}>
                                    Future date — offboarding will be scheduled and applied on this day. You can cancel from the Offboarding tab before then.
                                </p>
                            )}
                            <label style={{ ...labelStyle, marginTop: 14 }}>Reason *</label>
                            <select value={reason} onChange={e => setReason(e.target.value)} style={inputStyle}>
                                {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                            <label style={{ ...labelStyle, marginTop: 14 }}>Eligible for rehire?</label>
                            <div style={{ display: 'flex', gap: 12 }}>
                                <label style={{ fontSize: 14 }}><input type="radio" checked={rehireEligible} onChange={() => setRehireEligible(true)} /> Yes</label>
                                <label style={{ fontSize: 14 }}><input type="radio" checked={!rehireEligible} onChange={() => setRehireEligible(false)} /> No</label>
                            </div>
                            <label style={{ ...labelStyle, marginTop: 14 }}>Notes</label>
                            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} style={inputStyle} placeholder="Optional exit notes" />
                        </div>
                    )}

                    {step === 1 && (
                        <div>
                            <p style={{ fontSize: 14, color: '#475569', marginTop: 0 }}>How will final pay be handled?</p>
                            {FINAL_PAY.map(opt => (
                                <label key={opt.value} style={{
                                    display: 'block', border: `1.5px solid ${finalPayPath === opt.value ? '#1B75F0' : '#e2e8f0'}`,
                                    borderRadius: 10, padding: 12, marginBottom: 8, cursor: 'pointer',
                                    background: finalPayPath === opt.value ? '#EFF6FF' : '#fff',
                                }}>
                                    <input type="radio" name="finalPay" checked={finalPayPath === opt.value} onChange={() => setFinalPayPath(opt.value)} style={{ marginRight: 8 }} />
                                    <strong style={{ fontSize: 14 }}>{opt.label}</strong>
                                    <div style={{ fontSize: 12, color: '#64748b', marginLeft: 22 }}>{opt.desc}</div>
                                </label>
                            ))}
                            <label style={{ ...labelStyle, marginTop: 12 }}>Unused PTO / leave payout amount (optional)</label>
                            <input type="number" min="0" step="0.01" value={ptoAmount} onChange={e => setPtoAmount(e.target.value)} style={inputStyle} placeholder="0.00" />
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            {isContractor ? (
                                <p style={{ fontSize: 14, color: '#475569' }}>Contractors typically have no benefit enrollments. Continue to confirm portal access.</p>
                            ) : (
                                <>
                                    <p style={{ fontSize: 14, color: '#475569', marginTop: 0 }}>Select benefits to deactivate on the effective date.</p>
                                    {empBenefits.length === 0 && <p style={{ fontSize: 13, color: '#94a3b8' }}>No active enrollments.</p>}
                                    {empBenefits.map(eb => (
                                        <label key={eb.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: 14 }}>
                                            <input type="checkbox" checked={!!deactivateBenefits[eb.id]} onChange={e => setDeactivateBenefits(s => ({ ...s, [eb.id]: e.target.checked }))} />
                                            {eb.planName}
                                        </label>
                                    ))}
                                    {cobraBenefits.length > 0 && (
                                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginTop: 12, fontSize: 13, color: '#991b1b' }}>
                                            Qualifying health coverage detected. COBRA election notice should be sent within 14 days. Election deadline ~{cobraDeadline}. Track details on the COBRA / Offboarding tab.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <p style={{ fontSize: 14, color: '#475569', marginTop: 0 }}>
                                Portal access is revoked on the <strong>effective date</strong> ({fmt(termDate)}).
                                Until then the worker remains active if the date is in the future.
                            </p>
                            <ul style={{ fontSize: 13, color: '#64748b', paddingLeft: 18 }}>
                                <li>Login blocked once offboarding is applied</li>
                                <li>Payroll excludes terminated workers by termination date</li>
                                <li>History and tax records are retained</li>
                            </ul>
                        </div>
                    )}

                    {step === 4 && done && results && (
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 17, fontWeight: 700, color: '#374151' }}>
                                {results.isScheduled ? 'Offboarding Scheduled' : 'Offboarding Complete'}
                            </div>
                            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                                {personName} · effective {fmt(results.termDate)}
                            </div>
                            <div style={{ textAlign: 'left', marginTop: 16, fontSize: 13, color: '#475569' }}>
                                <div>Final pay: <strong>{FINAL_PAY.find(f => f.value === results.finalPayPath)?.label}</strong></div>
                                <div>Reason: <strong>{REASONS.find(r => r.value === results.reason)?.label}</strong></div>
                                {results.benefitsDeactivated?.length > 0 && (
                                    <div>Benefits deactivated: {results.benefitsDeactivated.join(', ')}</div>
                                )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}>
                                {results.finalPayPath === 'off_cycle' && (
                                    <button type="button" onClick={() => { onClose(); navigate(routes.DASHBOARD.START_NEW_PAYROLL); }}
                                        style={{ background: '#1B75F0', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 600, cursor: 'pointer' }}>
                                        Go to Run Payroll
                                    </button>
                                )}
                                <button type="button" onClick={() => { onClose(); navigate('/employees/offboarding'); }}
                                    style={{ background: '#fff', color: '#1B75F0', border: '1px solid #1B75F0', borderRadius: 8, padding: '10px 16px', fontWeight: 600, cursor: 'pointer' }}>
                                    Open Offboarding checklist
                                </button>
                                <button type="button" onClick={onClose}
                                    style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer' }}>
                                    Close
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {!done && (
                    <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
                        <button type="button" disabled={saving || step === 0} onClick={() => setStep(s => s - 1)}
                            style={{ background: 'none', border: 'none', color: '#64748b', fontWeight: 600, cursor: step === 0 ? 'default' : 'pointer', opacity: step === 0 ? 0.4 : 1 }}>
                            Back
                        </button>
                        {step < 3 ? (
                            <button type="button" onClick={() => setStep(s => s + 1)}
                                style={{ background: '#1B75F0', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}>
                                Next
                            </button>
                        ) : (
                            <button type="button" disabled={saving || !termDate || !finalPayPath} onClick={handleFinish}
                                style={{ background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                                {saving ? 'Saving…' : (isScheduled ? 'Schedule Offboarding' : 'Complete Offboarding')}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
